import { useRef, useState } from 'react';
import { Button, CircularProgress, IconButton } from '@mui/material';
import { LuTrash2, LuUpload } from 'react-icons/lu';
import { hideOnImgError, imageUrl } from '@utils/imageUrl';
import type { ImageFolder } from '@features/images/api';
import { useUploadImage } from '@features/images/hooks';

interface Props {
  folder: ImageFolder;
  imageStorageKey: string | null;
  thumbnailStorageKey: string | null;
  onChange: (next: {
    imageStorageKey: string | null;
    thumbnailStorageKey: string | null;
  }) => void;
  error?: string;
  disabled?: boolean;
}

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT_ATTR = ALLOWED_MIME.join(',');
const PREVIEW_HEIGHT = 'h-56'; // matches the empty drop-zone height for visual continuity

function describeMime(type: string): string {
  if (!type) return 'unknown';
  return type.replace(/^image\//, '').toUpperCase();
}

function formatMb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function ImageUploadField({
  folder,
  imageStorageKey,
  thumbnailStorageKey,
  onChange,
  error,
  disabled,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadImage(folder);
  const [localError, setLocalError] = useState<string | null>(null);

  const previewUrl = imageUrl(thumbnailStorageKey ?? imageStorageKey);
  const busy = upload.isPending || disabled;
  const errorMsg = localError ?? error ?? null;

  const pick = () => {
    setLocalError(null);
    fileInputRef.current?.click();
  };

  const handleFile = async (file: File) => {
    if (!ALLOWED_MIME.includes(file.type)) {
      setLocalError(
        `${describeMime(file.type)} isn't supported — use JPG, PNG, or WebP.`,
      );
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError(
        `${formatMb(file.size)} is too big — max is 5 MB. Try compressing or resizing.`,
      );
      return;
    }
    try {
      const result = await upload.mutateAsync(file);
      onChange(result);
    } catch {
      // toast already shown by useUploadImage
    }
  };

  const clear = () =>
    onChange({ imageStorageKey: null, thumbnailStorageKey: null });

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          // Allow re-selecting the same file after a clear / failed upload.
          e.target.value = '';
        }}
      />

      {previewUrl ? (
        <div
          className={`relative ${PREVIEW_HEIGHT} w-full overflow-hidden rounded-lg border-2 border-dashed border-border bg-surface-muted/40`}
        >
          {/* Blurred copy of the image fills the dead space behind the contained
              preview, so portrait/odd-aspect images don't sit in a flat-grey void. */}
          <div
            aria-hidden
            className="absolute inset-0 scale-110 bg-cover bg-center opacity-40 blur-2xl"
            style={{ backgroundImage: `url(${previewUrl})` }}
          />
          <img
            src={previewUrl}
            alt="Article hero preview"
            className="relative h-full w-full object-contain"
            onError={hideOnImgError}
          />
          {busy && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/70">
              <CircularProgress size={24} />
            </div>
          )}
          <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5">
            <Button
              variant="contained"
              size="small"
              onClick={pick}
              disabled={busy}
              startIcon={<LuUpload size={14} />}
              sx={{
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper', boxShadow: 2 },
              }}
            >
              Replace
            </Button>
            <IconButton
              size="small"
              onClick={clear}
              disabled={busy}
              aria-label="Remove image"
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper', boxShadow: 2 },
              }}
            >
              <LuTrash2 size={16} />
            </IconButton>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className={`flex ${PREVIEW_HEIGHT} w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-surface-muted/40 px-4 text-foreground-muted transition-colors hover:border-primary/40 hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {busy ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <LuUpload size={20} />
              <span className="text-[13px] font-medium">Upload image</span>
              <span className="text-[11px]">JPG, PNG or WebP, up to 5 MB</span>
            </>
          )}
        </button>
      )}

      {errorMsg && (
        <p className="mt-2 text-[12px] text-danger">{errorMsg}</p>
      )}
    </div>
  );
}
